import { toast } from "sonner";
import { rawApi } from "@/lib/offline/net";
import { enqueuePhoto } from "@/lib/offline/sync";
import { getState } from "@/lib/offline/store";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function resizeImage(file, maxDim, quality, type = "image/webp") {
  const img = await loadImage(file);
  const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((b) => {
      if (b) { resolve({ blob: b, w, h }); return; }
      // iOS Safari can't encode webp via canvas (returns null) → fall back to JPEG
      // so an upload never gets an empty blob. This is a common cause of "upload not working".
      canvas.toBlob((jb) => resolve({ blob: jb, w, h }), "image/jpeg", quality);
    }, type, quality);
  });
}

let lowStorageWarned = false;
async function warnIfLowStorage() {
  if (lowStorageWarned) return;
  try {
    if (navigator.storage?.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      if (quota && usage / quota > 0.9) {
        lowStorageWarned = true;
        toast("Storage almost full — reconnect soon to sync your photos");
      }
    }
  } catch { /* ignore */ }
}

// Uploads photos with client-side WebP resize. When offline (or if the network
// drops mid-upload) the photo is queued in IndexedDB and auto-sent on reconnect.
export async function uploadPhotos(files, { endpoint = "/photos/upload", fields = {}, onEach, onError, concurrency = 3 } = {}) {
  const tasks = Array.from(files);
  let idx = 0;

  async function queueOffline(full, thumb) {
    const photo = await enqueuePhoto({ endpoint, fields, fullBlob: full.blob, thumbBlob: thumb.blob, w: full.w, h: full.h });
    warnIfLowStorage();
    onEach && onEach(photo);
  }

  async function runOne(file) {
    const [full, thumb] = await Promise.all([
      resizeImage(file, 1600, 0.82),
      resizeImage(file, 400, 0.7),
    ]);

    // Preserve ordering: if anything is already queued, this must queue too.
    if (!navigator.onLine || getState().pending > 0) {
      await queueOffline(full, thumb);
      return;
    }

    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
        fd.append("width", String(full.w));
        fd.append("height", String(full.h));
        fd.append("full", full.blob, "full.webp");
        fd.append("thumb", thumb.blob, "thumb.webp");
        const { data } = await rawApi.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onEach && onEach(data);
        return;
      } catch (e) {
        if (!e?.response) { await queueOffline(full, thumb); return; } // network drop → queue
        if (attempt === 3) { onError && onError(e); throw e; }
        await sleep(400 * 2 ** attempt);
      }
    }
  }

  async function lane() {
    while (idx < tasks.length) {
      const my = idx++;
      await runOne(tasks[my]);
    }
  }

  await Promise.all(Array.from({ length: concurrency }, lane));
}
